import { Handle, Position } from "@xyflow/react";
import { Box, Boxes } from "lucide-react";
import { useState } from "react";
import { containerMatchesOverlay, type OverlayMode, overlayRingClass } from "@/lib/arch-overlay";
import type { Container } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { useArchViz } from "../ArchVizContext";
import { brandIconSlugFromName, brandIconUrl } from "./brand-icons";
import { CommentBadge } from "./CommentBadge";
import { containerPalette } from "./node-colors";
import { RiskBadge } from "./RiskBadge";

function TechChip({ tech }: { tech: string }) {
  const slug = brandIconSlugFromName(tech);
  const [failed, setFailed] = useState(false);
  return (
    <span className="text-[10px] rounded-xs border border-border bg-surface-tint text-ink-subtle px-1.5 py-0.5 font-mono inline-flex items-center gap-1">
      {slug && !failed && (
        <img
          src={brandIconUrl(slug)}
          alt=""
          loading="lazy"
          className="h-2.5 w-2.5 object-contain"
          onError={() => setFailed(true)}
        />
      )}
      {tech}
    </span>
  );
}

export interface ContainerNodeData {
  container: Container;
  faded?: boolean;
  highlighted?: boolean;
  activeOverlay?: OverlayMode;
  // Precomputed set of external-system IDs this container connects to.
  // Set in use-graph-elements where the v0.2/v0.3 edge sources are normalized;
  // never read container.connects_to directly here.
  connectsToExternals?: ReadonlySet<string>;
  commentCount?: number;
  carryOverCount?: number;
  [key: string]: unknown;
}

export function ContainerNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: ContainerNodeData;
  selected?: boolean;
}) {
  const { state, dispatch } = useArchViz();
  const palette = containerPalette(data.container.kind);
  const componentCount = data.container.components.length;
  const overlay = data.activeOverlay ?? "none";
  const overlayMatch =
    overlay !== "none" &&
    containerMatchesOverlay(data.container, overlay, data.connectsToExternals ?? new Set<string>());
  const riskCount = data.container.risks?.length ?? data.container.risk.length;
  return (
    <div
      className={cn(
        "relative rounded-xl border-[1.5px] px-4 py-3 shadow-sm transition-all min-w-[220px] max-w-[280px]",
        palette.bg,
        palette.border,
        palette.text,
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70",
        overlayMatch && overlay !== "none" && overlayRingClass(overlay),
      )}
    >
      <div className="absolute -top-3.5 left-2 text-[9px] uppercase tracking-eyebrow font-semibold text-muted-foreground whitespace-nowrap pointer-events-none">
        {data.container.kind ?? data.container.type}
      </div>
      <CommentBadge count={data.commentCount ?? 0} carryOver={data.carryOverCount ?? 0} />
      {state.showRisks && (
        <RiskBadge
          count={riskCount}
          onClick={() => dispatch({ type: "SELECT_NODE_FOR_RISKS", nodeId: id })}
        />
      )}
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Bottom} id="bottom" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Right} id="right" className="!bg-foreground/30" />
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {componentCount > 0 ? (
            <Boxes className="h-5 w-5 shrink-0 mt-0.5" />
          ) : (
            <Box className="h-5 w-5 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <div
              className="font-semibold leading-none tracking-tight"
              style={{ fontSize: "calc(14px * var(--archviz-label-scale, 1))" }}
            >
              {data.container.label}
            </div>
            {(data.container.headline ?? data.container.description) && (
              // Clamp to 3 lines so the rendered card stays inside the
              // 140 px height the L2 layout reserves per container
              // (kind-rank-layout NODE_HEIGHT). Without this, a long
              // description grows the card vertically and the row below
              // overlaps. The full text is always available in the side
              // panel — the canvas just shows a preview.
              <div className="text-[10px] text-muted-foreground leading-snug mt-1 line-clamp-3">
                {data.container.headline ?? data.container.description}
              </div>
            )}
          </div>
        </div>
        {componentCount > 0 && (
          <div className="font-mono text-[9.5px] font-semibold rounded-sm bg-surface-tint text-ink-muted px-1.5 py-0.5 shrink-0">
            {componentCount}
          </div>
        )}
      </div>
      {(() => {
        const techList = data.container.tech_stack ?? data.container.technology;
        if (techList.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1 mt-2">
            {techList.slice(0, 2).map((tech) => (
              <TechChip key={tech} tech={tech} />
            ))}
            {techList.length > 2 && (
              <span className="text-[10px] text-ink-subtle self-center">
                +{techList.length - 2}
              </span>
            )}
          </div>
        );
      })()}
      <Handle type="source" position={Position.Top} id="top-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
      <Handle type="source" position={Position.Left} id="left-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-foreground/30"
      />
    </div>
  );
}
